import { RefObject, useCallback, useEffect, useRef, useState } from "react";

interface Edges {
  start: boolean;
  end: boolean;
}

// Reports which sides of a horizontal scroller still have content hidden.
// These scrollers hide their scrollbar, so nothing else tells the user that
// more is there — the caller draws its own affordance from `edges`.
export function useScrollEdges<T extends HTMLElement>(): {
  ref: RefObject<T>;
  edges: Edges;
  overflows: boolean;
} {
  const ref = useRef<T>(null);
  const [edges, setEdges] = useState<Edges>({ start: false, end: false });
  const [overflows, setOverflows] = useState(false);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const next = { start: el.scrollLeft > 1, end: el.scrollLeft < max - 1 };
    setEdges((prev) =>
      prev.start === next.start && prev.end === next.end ? prev : next
    );
    setOverflows(max > 1);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    measure();
    el.addEventListener("scroll", measure, { passive: true });
    const resize = new ResizeObserver(measure);
    resize.observe(el);
    // Content width changes without the scroller itself resizing. Observing
    // each child catches relabelling (counts) once per frame, and only when a
    // width actually moves; the MutationObserver just tracks which children
    // exist, so deep text churn never triggers a measure.
    Array.from(el.children).forEach((child) => resize.observe(child));
    const mutation = new MutationObserver((records) => {
      records.forEach((record) => {
        record.removedNodes.forEach((node) => {
          if (node instanceof Element) resize.unobserve(node);
        });
        record.addedNodes.forEach((node) => {
          if (node instanceof Element) resize.observe(node);
        });
      });
      measure();
    });
    mutation.observe(el, { childList: true });
    return () => {
      el.removeEventListener("scroll", measure);
      resize.disconnect();
      mutation.disconnect();
    };
  }, [measure]);

  return { ref, edges, overflows };
}
